import { createContext, useContext, useEffect, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';

const initialState: Partial<SettingsState> = {
    debugCards: false,
    debugEvaluation: false,
    debugPracticeLimit: false,
    debugProgress: false,
    debugStats: false,
    volume: 0.2,
};

const SettingsContext = createContext<SettingsState>({} as SettingsState);

export const useSettings = () => useContext(SettingsContext);

export function withSettings(WrappedComponent: any) {
    return function SettingsWrappedComponent(props: any) {
        return (
            <SettingsContext.Consumer>
                {state => <WrappedComponent {...props} settings={state} />}
            </SettingsContext.Consumer>
        );
    };
}

export function SettingsProvider({ children }: SettingsProviderProps) {
    const [ready, setReady] = useState(false);
    const [state, setState] = useLocalStorage<Partial<SettingsState>>('sp.settings', initialState);

    const reloaders: string[] = [];

    useEffect(() => {
        if (!ready) {
            setReady(true);

            // Check if keys match
            const initialKeys = Object.keys(initialState);
            const stateKeys = Object.keys(state);
            if (
                !initialKeys.every(key => stateKeys.includes(key)) ||
                !stateKeys.every(key => initialKeys.includes(key))
            ) {
                setState({
                    ...initialState,
                });
            }
        }
    }, [ready, state, setState]);

    function set(name: string, value: any) {
        // Update state
        setState({
            ...initialState,
            ...state,
            [name]: value,
        });

        // Reload page needed
        if (reloaders.includes(name)) {
            window.location.reload();
        }
    }

    const stateValue = {
        ...(process.env.NEXT_PUBLIC_ADMIN ? state : initialState),
        reloaders,
        set,
    } as SettingsState;

    return <SettingsContext.Provider value={stateValue}>{children}</SettingsContext.Provider>;
}
